import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  Briefcase,
  Building2,
  Clock,
  MapPin,
  ExternalLink,
  ShieldCheck,
  ArrowLeft,
  Facebook,
  Linkedin,
  MessageCircle,
  Copy,
  Check,
} from "lucide-react";
import { useState } from "react";
import { getPublicVacancy } from "@/lib/api/vacancies.functions";

const SITE = "https://nkadimengdannylcs.com";
const OG_DEFAULT = `${SITE}/og/careers-default.jpg`;

function fmtDate(d: string | null | undefined) {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString("en-ZA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return d;
  }
}

function shareCaption(v: {
  title: string;
  employer: string;
  location: string | null;
  employment_type: string | null;
  closing_date: string | null;
  description: string | null;
}) {
  const lines: string[] = [];
  lines.push(`💼 ${v.title} — ${v.employer}`);
  const meta: string[] = [];
  if (v.location) meta.push(`📍 ${v.location}`);
  if (v.employment_type) meta.push(`🕒 ${v.employment_type}`);
  if (v.closing_date) meta.push(`⏳ Closes ${fmtDate(v.closing_date)}`);
  if (meta.length) lines.push(meta.join(" • "));
  if (v.description) {
    const desc = v.description.replace(/\s+/g, " ").trim();
    lines.push(desc.length > 260 ? `${desc.slice(0, 257)}…` : desc);
  }
  lines.push("Apply via Nkadimeng Danny Legal — verified vacancies board.");
  return lines.join("\n\n");
}

export const Route = createFileRoute("/careers/$id")({
  loader: async ({ params }) => {
    const row = await getPublicVacancy({ data: { id: params.id } });
    if (!row) throw notFound();
    return row;
  },
  head: ({ params, loaderData }) => {
    const v = loaderData;
    if (!v) {
      return {
        meta: [{ title: "Vacancy — Nkadimeng Danny Legal" }],
      };
    }
    const url = `${SITE}/careers/${params.id}`;
    const title = `${v.title} — ${v.employer} | Vacancy`;
    const description =
      (v.description || `${v.title} at ${v.employer}${v.location ? `, ${v.location}` : ""}. Apply via Nkadimeng Danny Legal.`)
        .replace(/\s+/g, " ")
        .slice(0, 300);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:image", content: OG_DEFAULT },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: OG_DEFAULT },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "JobPosting",
            title: v.title,
            description,
            datePosted: v.posted_at,
            validThrough: v.closing_date || undefined,
            employmentType: v.employment_type || undefined,
            hiringOrganization: {
              "@type": "Organization",
              name: v.employer,
            },
            jobLocation: v.location
              ? {
                  "@type": "Place",
                  address: { "@type": "PostalAddress", addressLocality: v.location, addressCountry: "ZA" },
                }
              : undefined,
            url,
            directApply: !!v.application_url,
          }),
        },
      ],
    };
  },
  component: VacancyDetail,
});

function VacancyDetail() {
  const v = Route.useLoaderData();
  const params = Route.useParams();
  const shareUrl = `${SITE}/careers/${params.id}`;
  const caption = shareCaption(v as any);

  return (
    <div className="container-narrow py-10">
      <Link
        to="/careers"
        className="inline-flex items-center gap-1 text-sm text-navy hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Back to vacancies
      </Link>

      <article className="mt-6 rounded-lg border border-border bg-card p-6 shadow-soft">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold">Vacancy</p>
            <h1 className="mt-2 font-display text-3xl text-navy">{v.title}</h1>
            <p className="mt-2 flex items-center gap-1.5 text-foreground/80">
              <Building2 className="h-4 w-4" /> {v.employer}
            </p>
          </div>
          {v.sector && (
            <span className="rounded bg-navy/5 px-2 py-1 text-[11px] uppercase tracking-wider text-navy">
              {v.sector}
            </span>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-sm text-foreground/70">
          {v.location && (
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" /> {v.location}
            </span>
          )}
          {v.employment_type && (
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" /> {v.employment_type}
            </span>
          )}
          {v.salary_range && <span>{v.salary_range}</span>}
          {v.closing_date && <span>Closes {fmtDate(v.closing_date)}</span>}
        </div>

        {v.description && (
          <section className="mt-6">
            <h2 className="font-display text-lg text-navy">About the role</h2>
            <p className="mt-2 whitespace-pre-line text-foreground/85">{v.description}</p>
          </section>
        )}

        {v.requirements && (
          <section className="mt-6">
            <h2 className="font-display text-lg text-navy">Requirements</h2>
            <p className="mt-2 whitespace-pre-line text-foreground/85">{v.requirements}</p>
          </section>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-border/60 pt-4 text-xs">
          <span className="text-muted-foreground">
            Source: <span className="font-medium text-foreground/80">{v.source_name}</span>
          </span>
          {v.source_url && (
            <a
              href={v.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-navy hover:underline"
            >
              View original <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <a
            href={v.application_url}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1 rounded bg-navy px-4 py-2 text-sm font-medium text-navy-foreground hover:bg-navy/90"
          >
            Apply now <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" /> Verify the employer before
          paying any fees. Legitimate employers never charge applicants.
        </div>
      </article>

      <ShareBar url={shareUrl} caption={caption} title={v.title} />
    </div>
  );
}

function ShareBar({ url, caption, title }: { url: string; caption: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const enc = encodeURIComponent;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
  const wa = `https://wa.me/?text=${enc(`${caption}\n\n${url}`)}`;
  const li = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(`${caption}\n\n${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  }

  return (
    <section className="mt-6 rounded-lg border border-border bg-card p-5">
      <div className="flex items-center gap-2">
        <Briefcase className="h-4 w-4 text-navy" />
        <h2 className="font-display text-lg text-navy">Share this vacancy</h2>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Each platform pulls its own preview card from this page — including the
        job title, employer, and cover image.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <a
          href={fb}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full bg-[#1877F2] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          <Facebook className="h-4 w-4" /> Facebook
        </a>
        <a
          href={wa}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          <MessageCircle className="h-4 w-4" /> WhatsApp
        </a>
        <a
          href={li}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full bg-[#0A66C2] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          <Linkedin className="h-4 w-4" /> LinkedIn
        </a>
        <button
          type="button"
          onClick={copy}
          aria-label={`Copy share caption for ${title}`}
          className="inline-flex items-center gap-2 rounded-full border border-input px-4 py-2 text-sm font-medium hover:border-navy"
        >
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied" : "Copy caption"}
        </button>
      </div>
      <details className="mt-4 rounded border border-border/70 bg-muted/30 p-3 text-xs">
        <summary className="cursor-pointer font-medium text-foreground/80">
          Preview the caption
        </summary>
        <pre className="mt-2 whitespace-pre-wrap font-sans text-foreground/80">{caption}</pre>
      </details>
    </section>
  );
}
